/**
 * Data-fetching/normalising helpers for the Provision card (Story 56425). Keeps the section
 * components free of API-shape knowledge, mirroring ProfessionalDetails.helper.js.
 */
import * as api from "../../../../../Utils/apis/provisionApi";

/** @returns {import("../../../../../domain/provision/provision.types").ProvisionDetailsInput} */
export const emptyDetails = () => ({
  provisionAmount: null,
  provisionApprovalDate: null,
  gceNetOfProvision: null,
});

/** @returns {import("../../../../../domain/provision/provision.types").Professionals} */
export const emptyProfessionals = () => ({
  lawyerId: null,
  valuerId: null,
  surveyorId: null,
  fourthParty: null,
});

/**
 * Normalises whatever the API returns into a shape every section is guaranteed to have — so
 * the card never has to null-check `provision.details`/`provision.professionals` etc.
 * @param {string} loanId
 * @param {import("../../../../../domain/provision/provision.types").Provision} [raw]
 */
export function normaliseProvision(loanId, raw) {
  return {
    loanId,
    editable: Boolean(raw?.editable),
    details: { ...emptyDetails(), ...(raw?.details || {}) },
    professionals: { ...emptyProfessionals(), ...(raw?.professionals || {}) },
    scenarios: Array.isArray(raw?.scenarios) ? raw.scenarios : [],
    provisionOutcomePct: raw?.provisionOutcomePct ?? null,
    notes: raw?.notes ?? "",
    dcfDocuments: Array.isArray(raw?.dcfDocuments) ? raw.dcfDocuments : [],
    audit: raw?.audit || null,
  };
}

/**
 * Builds the PUT /loans/{loanId}/provision body from the three editable drafts — computed
 * fields (ratios, outcome) and audit are never sent (claude-code-UI-prompt.md: "Computed fields
 * are display-only; never post them back").
 * @param {import("../../../../../domain/provision/provision.types").ProvisionDetailsInput} details
 * @param {import("../../../../../domain/provision/provision.types").Professionals} professionals
 * @param {string|null} notes
 * @returns {import("../../../../../domain/provision/provision.types").ProvisionUpsert}
 */
export function buildUpsertPayload(details, professionals, notes) {
  return {
    details: {
      provisionAmount: details.provisionAmount === "" ? null : details.provisionAmount,
      provisionApprovalDate: details.provisionApprovalDate || null,
      gceNetOfProvision: details.gceNetOfProvision === "" ? null : details.gceNetOfProvision,
    },
    professionals,
    notes: notes || null,
  };
}

export { api };
